import { useMutation } from '@tanstack/react-query';
import React, { useCallback, useEffect, useReducer, useState } from 'react';
import QRCode from 'react-qr-code';

import { Button, Tooltip, TooltipContent, TooltipProvider, TooltipTrigger, cn } from '@column/ui';

import { CodeInput, Loading } from '~/components';
import { RecaptchaAction, useReCAPTCHA } from '~/lib/recaptcha';
import { UserModel } from '~/models';
import { FactorType, MFAStatus, UserRepository } from '~/repositories';
import { useNotificationStore } from '~/stores/Notification';
import { useSessionStore } from '~/stores/Session';
import { copyToClipboard } from '~/util';

const DIVIDER_CLASS_NAME = 'border-gray-100 dark:border-white/[0.08]';
const COPIED_CONFIRMATION_MS = 1500;

const getSetupCode = (otpauthUri: string) => {
  try {
    return new URL(otpauthUri).searchParams.get('secret') ?? otpauthUri;
  } catch {
    return otpauthUri;
  }
};

interface ActionInit {
  action: 'Init';
}

interface ActionStartFactorSetup {
  action: 'StartFactorSetup';
  user: UserModel;
}

interface ActionCompleteFactorSetup {
  action: 'CompleteFactorSetup';
  factorId: string;
  otpauthUri: string;
}

interface ActionStartFactorVerification {
  action: 'StartFactorVerification';
}

interface ActionFailFactorVerification {
  action: 'FailFactorVerification';
}

interface ActionCompleteSetup {
  action: 'CompleteSetup';
}

interface ActionError {
  action: 'Error';
  message: string;
}

type MfaAction =
  | ActionInit
  | ActionStartFactorSetup
  | ActionCompleteFactorSetup
  | ActionStartFactorVerification
  | ActionFailFactorVerification
  | ActionCompleteSetup
  | ActionError;

interface StateInit {
  state: 'Init';
}

interface StateAwaitingSetup {
  state: 'AwaitingSetup';
  user: UserModel;
}

interface StateAwaitingCodeInput {
  state: 'AwaitingCodeInput';
  factorId: string;
  otpauthUri: string;
  user: UserModel;
}

interface StateAwaitingVerification {
  state: 'AwaitingVerification';
  factorId: string;
  otpauthUri: string;
  user: UserModel;
}

interface StateComplete {
  state: 'Complete';
  otpauthUri?: string;
}

interface StateError {
  state: 'Error';
  message: string;
}

type MfaSetupState =
  | StateInit
  | StateAwaitingSetup
  | StateAwaitingCodeInput
  | StateAwaitingVerification
  | StateComplete
  | StateError;

const InitState: StateInit = { state: 'Init' };

const mfaSetupReducer = (state: MfaSetupState, action: MfaAction): MfaSetupState => {
  switch (action.action) {
    case 'Init':
      return { state: 'Init' };
    case 'StartFactorSetup':
      return { state: 'AwaitingSetup', user: action.user };
    case 'CompleteFactorSetup':
      if (state.state !== 'AwaitingSetup') {
        return { state: 'Error', message: 'Invalid state' };
      }
      return {
        state: 'AwaitingCodeInput',
        factorId: action.factorId,
        otpauthUri: action.otpauthUri,
        user: state.user,
      };
    case 'StartFactorVerification':
      if (state.state !== 'AwaitingCodeInput') {
        return { state: 'Error', message: 'Invalid state' };
      }
      return {
        state: 'AwaitingVerification',
        factorId: state.factorId,
        otpauthUri: state.otpauthUri,
        user: state.user,
      };
    case 'FailFactorVerification':
      if (state.state !== 'AwaitingVerification') {
        return { state: 'Error', message: 'Invalid state' };
      }
      return {
        state: 'AwaitingCodeInput',
        factorId: state.factorId,
        otpauthUri: state.otpauthUri,
        user: state.user,
      };
    case 'CompleteSetup':
      return {
        state: 'Complete',
        otpauthUri:
          state.state === 'AwaitingCodeInput' || state.state === 'AwaitingVerification' ? state.otpauthUri : undefined,
      };
    case 'Error':
      return { state: 'Error', message: action.message };
  }
};

interface SetupDeviceMFAProps {
  enabled?: boolean;
  fullWidth?: boolean;
  onSetupComplete?: () => void;
  onSuccess?: () => void;
  errorNotificationDisplay?: string;
  successNotificationDisplay?: string;
}

export const SetupDeviceMFA: React.FC<SetupDeviceMFAProps> = ({
  enabled = true,
  fullWidth = false,
  onSetupComplete,
  onSuccess,
  errorNotificationDisplay = 'page',
  successNotificationDisplay = errorNotificationDisplay,
}) => {
  const { currentUser: user, refetchCurrentUser, setMfaStatus } = useSessionStore();
  const { addDangerNotification, addSuccessNotification } = useNotificationStore();
  const executeReCAPTCHA = useReCAPTCHA();

  const [verificationCode, setVerificationCode] = useState<string>('');
  const [mfaSetupState, dispatch] = useReducer(mfaSetupReducer, InitState);
  const [showCopied, setShowCopied] = useState<boolean>(false);

  const setupMfaMutation = useMutation({
    mutationFn: async (userModel: UserModel) => {
      const reCaptchaToken = await executeReCAPTCHA(RecaptchaAction.MfaSetup, { optional: true });
      return UserRepository.setupMfa(reCaptchaToken, {
        factorName: userModel.email,
        factorType: FactorType.TOTP,
      });
    },
    onError: (error: any) => {
      dispatch({ action: 'Error', message: (error as Error).message });
    },
    onSuccess: (resp: { factorId?: string; otpauthUri?: string }) => {
      const { factorId, otpauthUri: otpauthUriResp } = resp;
      if (!factorId || !otpauthUriResp) {
        dispatch({ action: 'Error', message: 'An error occurred during setup. Please try again.' });
        return;
      }
      dispatch({ action: 'CompleteFactorSetup', factorId, otpauthUri: otpauthUriResp });
    },
  });

  const verifyMfaMutation = useMutation({
    mutationFn: async ({ code, factorId }: { code: string; factorId: string }) =>
      UserRepository.verifyMfa({
        code,
        factorId,
      }),
    onError: (error: any) => {
      let content = (error as Error)?.message ?? 'An error occurred';
      if (content === 'Incorrect login mfa code.') {
        content = 'Invalid verification code';
      }
      addDangerNotification({
        content,
        display: errorNotificationDisplay,
      });
      dispatch({ action: 'FailFactorVerification' });
      setVerificationCode('');
    },
    onSuccess: (resp: { mfaStatus: MFAStatus }) => {
      onSetupComplete?.();
      setMfaStatus(resp.mfaStatus);

      addSuccessNotification({
        content: 'Authenticator app enabled',
        display: successNotificationDisplay,
      });
      dispatch({ action: 'CompleteSetup' });
    },
  });

  const handleCopySetupCode = useCallback(
    (otpauthUriParam: string) => {
      copyToClipboard(getSetupCode(otpauthUriParam))
        .then(() => setShowCopied(true))
        .catch(() =>
          addDangerNotification({ content: 'Failed to copy the setup code', display: errorNotificationDisplay })
        );
    },
    [addDangerNotification, errorNotificationDisplay]
  );

  useEffect(() => {
    if (!showCopied) {
      return undefined;
    }
    const timer = setTimeout(() => setShowCopied(false), COPIED_CONFIRMATION_MS);
    return () => clearTimeout(timer);
  }, [showCopied]);

  useEffect(() => {
    if (enabled && user && mfaSetupState.state === 'Init') {
      dispatch({ action: 'StartFactorSetup', user });
    }
  }, [enabled, user, mfaSetupState.state]);

  useEffect(() => {
    if (mfaSetupState.state === 'AwaitingSetup' && !setupMfaMutation.isPending) {
      setupMfaMutation.mutate(mfaSetupState.user);
    }
    if (mfaSetupState.state === 'AwaitingCodeInput' && verificationCode.length === 6) {
      dispatch({ action: 'StartFactorVerification' });
    }
    if (mfaSetupState.state === 'AwaitingVerification' && !verifyMfaMutation.isPending) {
      verifyMfaMutation.mutate({
        code: verificationCode,
        factorId: mfaSetupState.factorId,
      });
    }
  }, [mfaSetupState, verificationCode, setupMfaMutation, verifyMfaMutation]);

  useEffect(() => {
    if (mfaSetupState.state === 'Error') {
      addDangerNotification({
        content: mfaSetupState.message,
        display: errorNotificationDisplay,
      });
    }
  }, [mfaSetupState, addDangerNotification, errorNotificationDisplay]);

  useEffect(() => {
    if (mfaSetupState.state === 'Complete') {
      refetchCurrentUser().then(() => {
        onSuccess?.();
      });
    }
  }, [mfaSetupState, onSuccess, refetchCurrentUser]);

  const isLoading = mfaSetupState.state === 'Init' || mfaSetupState.state === 'AwaitingSetup';
  const isAwaitingCodeInput =
    mfaSetupState.state === 'AwaitingCodeInput' || mfaSetupState.state === 'AwaitingVerification';
  const isVerifying = mfaSetupState.state === 'AwaitingVerification';
  const otpauthUri =
    mfaSetupState.state === 'AwaitingCodeInput' ||
    mfaSetupState.state === 'AwaitingVerification' ||
    mfaSetupState.state === 'Complete'
      ? mfaSetupState.otpauthUri
      : undefined;
  const isComplete = mfaSetupState.state === 'Complete';

  const showLoading = isLoading || !(isAwaitingCodeInput || otpauthUri);

  const intro = (
    <p className={cn('m-0 text-sm text-muted-foreground', fullWidth ? 'max-w-[440px] text-center' : 'text-left')}>
      Scan the QR code below with your preferred authenticator app. We recommend 1Password, Authy, or Google
      Authenticator.
    </p>
  );

  const codeInput = (
    <>
      <div className="mb-2 text-sm font-medium text-pretty text-foreground">Enter the code generated by your app</div>
      <CodeInput
        autoFocus
        fullWidth
        hideCaret
        isComplete={isComplete}
        isDisabled={isComplete}
        isLoading={isVerifying}
        placeholder="######"
        showPlaceholderOnFocus
        type="number"
        value={verificationCode}
        onChange={setVerificationCode}
      />
    </>
  );

  const copySetupCode = (
    <TooltipProvider>
      <Tooltip open={showCopied}>
        <TooltipTrigger asChild>
          <Button
            type="button"
            variant="secondary"
            size="xs"
            data-cy="copy-setup-code"
            onClick={() => handleCopySetupCode(otpauthUri ?? '')}
          >
            Copy setup code
          </Button>
        </TooltipTrigger>
        <TooltipContent side="bottom">Copied!</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );

  const setupCodeHint = "If your authenticator doesn't recognize the QR code, copy this setup code";

  if (fullWidth) {
    return (
      <div className="relative w-full overflow-hidden rounded-lg bg-gray-25 ring-1 ring-black/5 dark:bg-white/[0.02] dark:ring-white/10">
        <Loading centerSpinner isLoading={showLoading}>
          <div className="grid">
            <div className={cn('flex w-full justify-center border-b px-6 py-[18px]', DIVIDER_CLASS_NAME)}>{intro}</div>
            <div className="grid w-full grid-cols-1 md:grid-cols-[180px_minmax(0,1fr)]">
              <div
                className={cn(
                  'flex items-center justify-center px-6 pt-8 text-foreground md:min-h-[180px] md:border-r md:p-5',
                  DIVIDER_CLASS_NAME
                )}
              >
                <QRCode fgColor="currentColor" bgColor="transparent" size={140} value={otpauthUri ?? ''} />
              </div>
              <div className="flex min-w-0 flex-col items-center justify-center p-6">{codeInput}</div>
            </div>
            <div
              className={cn(
                'flex min-h-[60px] w-full flex-col items-center justify-between gap-2 border-t px-5 py-3 md:flex-row',
                DIVIDER_CLASS_NAME
              )}
            >
              <p className="m-0 mb-0.5 max-w-[260px] text-left text-xs leading-4 text-gray-500 dark:text-gray-400">
                {setupCodeHint}
              </p>
              {copySetupCode}
            </div>
          </div>
        </Loading>
      </div>
    );
  }

  return (
    <Loading isLoading={showLoading}>
      <div className="grid gap-4">
        {intro}
        <div className="flex items-center justify-center rounded-lg bg-gray-25 ring-1 ring-black/5 md:justify-start dark:bg-white/[0.02] dark:ring-white/10">
          <div className="grid w-full grid-cols-1 md:grid-cols-[min-content_minmax(0,1fr)]">
            <div
              className={cn(
                'flex items-center justify-center px-6 pt-8 text-foreground md:border-r md:p-6',
                DIVIDER_CLASS_NAME
              )}
            >
              <QRCode fgColor="currentColor" bgColor="transparent" size={150} value={otpauthUri ?? ''} />
            </div>
            <div className="flex flex-col items-center p-6 text-center md:items-start md:text-left">
              {codeInput}
              <div className="mt-4 flex flex-col items-center gap-2 md:items-start">
                <p className="m-0 mb-0.5 text-xs leading-4 text-gray-500 dark:text-gray-400">{setupCodeHint}</p>
                {copySetupCode}
              </div>
            </div>
          </div>
        </div>
      </div>
    </Loading>
  );
};
